/* Modello "preparato": avvolge ogni FlowStep del documento con i campi calcolati
   che servono a layout e rendering (numerazione, fase, blocco, riga…).
   Nell'originale questi campi erano prefissi `_` mutati sugli oggetti FLOW; qui
   vivono su oggetti interni distinti così il documento resta pulito. */
import type { DiagramDoc, Doc, FlowStep, Participant } from "../doc-schema.js";

export type Narrow<T extends FlowStep["t"]> = Extract<FlowStep, { t: T }>;

/** Evento preparato: riferimento allo step originale + campi calcolati. */
export interface PEvent {
  raw: FlowStep;
  t: FlowStep["t"];
  i: number;
  phase: PPhase | null;
  block: PBlock | null;
  branch: PBranch | null;
  row: PRow | null;
  path: { kind: string; cond: string }[];
  // msg
  n?: number;
  docs?: string[];
  search?: string;
  lines?: string[];
  // create/destroy
  standalone?: boolean;
  // stato transitorio del layout
  y?: number;
}

export interface PPhase {
  id: string;
  title: string;
  sub?: string;
  num: number;
  msgs: PEvent[];
  ev: PEvent;
  // stato transitorio del layout
  y?: number;
  visible?: boolean;
  closed?: boolean;
}

export interface PBranch {
  cond: string;
  msgs: PEvent[];
  ev: PEvent;
  visible?: boolean;
}

export interface PBlock {
  kind: "alt" | "opt" | "loop";
  depth: number;
  branches: PBranch[];
  cols: Set<number>;
  ev: PEvent;
  idx: number;
  cur: PBranch;
  endEv?: PEvent;
  // stato transitorio del layout
  frame?: Frame;
  visible?: boolean;
  firstVisibleBranch?: PBranch;
  y0?: number;
}

export interface PRow {
  kind: "msg" | "note";
  msgs: PEvent[];
  sync: string | null;
  block: PBlock | null;
  branch: PBranch | null;
  phase: PPhase | null;
  ev?: PEvent; // per le note
  hasCreate: boolean;
  hasDestroy: boolean;
  // stato transitorio del layout
  _y?: number;
  _on?: boolean;
  _drawn?: boolean;
}

export interface PreparedModel {
  doc: DiagramDoc;
  events: PEvent[];
  MSGS: PEvent[];
  PHASES: PPhase[];
  BLOCKS: PBlock[];
  ROWS: PRow[];
  partIdx: Map<string, number>;
  partById: Map<string, Participant>;
  docById: Map<string, Doc>;
  width: number;
  total: number;
}

/* ---- output del layout ---- */

export interface Frame {
  b: PBlock;
  y0: number;
  dividers: number[];
  branchY: { br: PBranch; y: number; head: boolean }[];
  y1?: number;
  x0?: number;
  x1?: number;
}

export interface LayoutRow {
  kind: "msg";
  row: PRow;
  msgs: PEvent[];
  sync: string | null;
  y: number;
  dim: boolean;
  label: string;
}

export interface LayoutBar {
  a: string;
  depth: number;
  y0: number;
  y1: number;
  openedAt: number;
  key: number;
}

export interface LayoutNote {
  ev: PEvent;
  x: number;
  y: number;
  w: number;
  h: number;
  lines: string[];
  dim: boolean;
}

export interface LayoutPhase {
  p: PPhase;
  y: number;
}

export interface LayoutSeg {
  a: string;
  boxY: number;
  y0: number;
  y1?: number;
  key: number;
  inline: boolean;
  dim: boolean;
  suppressed: boolean;
}

export interface LayoutOut {
  rows: LayoutRow[];
  bars: LayoutBar[];
  frames: Frame[];
  notes: LayoutNote[];
  phases: LayoutPhase[];
  segs: LayoutSeg[];
  height: number;
}

/** Stato UI (filtri, modalità, tema, selezione…). */
export interface DiagramState {
  actors: Set<string>;
  phases: Set<string>;
  docs: Set<string>;
  collapsed: Set<string>;
  q: string;
  mode: "dim" | "hide";
  zoom: number;
  sel: number | null;
  theme: "dark" | "light";
}

export function initialState(): DiagramState {
  return {
    actors: new Set(),
    phases: new Set(),
    docs: new Set(),
    collapsed: new Set(),
    q: "",
    mode: "dim",
    zoom: 1,
    sel: null,
    theme: "dark",
  };
}
